import React, { useEffect, useRef, useState } from 'react'
import {
  CButton,
  CCol,
  CRow,
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
  CModal,
  CModalHeader,
  CModalBody,
  CModalFooter,
  CSpinner,
  CFormInput,
  CCardHeader,
  CBadge,
} from '@coreui/react'
import CIcon from '@coreui/icons-react'
import { cilCloudDownload } from '@coreui/icons'
import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'
import { useDispatch, useSelector } from 'react-redux'
import { getCustomerInvoice, getTaxInvoices } from 'src/redux/taxinvoice'
import { DateTimeConverter, convertToWords, fileName } from 'src/utils/function'
import ReactHTMLTableToExcel from 'react-html-table-to-excel'

const TaxInvoice = () => {
  const reportRef = useRef(null)
  const dispatch = useDispatch()
  const { invoices, invoiceData, loadingInvoices } = useSelector((state) => state.customerinvoice)

  const [visible, setVisible] = useState(false)
  const [loading, setLoading] = useState(false)
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')

  useEffect(() => {
    const today = new Date()
    const thirtyDaysAgo = new Date()
    thirtyDaysAgo.setDate(today.getDate() - 30)

    const formattedToday = today.toISOString().split('T')[0]
    const formattedThirtyDaysAgo = thirtyDaysAgo.toISOString().split('T')[0]

    setStartDate(formattedThirtyDaysAgo)
    setEndDate(formattedToday)
  }, [])

  useEffect(() => {
    if (startDate && endDate) {
      dispatch(getTaxInvoices(startDate, endDate))
    }
  }, [dispatch, startDate, endDate])

  const calculateTotals = () => {
    let subtotal = 0
    let gstTotal = 0
    const itemsWithTotal = invoiceData?.items?.map((item) => {
      const itemTotal = item.qty * item.rate
      const gstAmount = (itemTotal * item.gstPercent) / 100
      subtotal += itemTotal
      gstTotal += gstAmount
      return { ...item, itemTotal, gstAmount }
    })
    const total = subtotal + invoiceData?.deliveryCharge - invoiceData?.discount
    // console.log(subtotal, gstTotal, invoiceData?.deliveryCharge, invoiceData?.discount)
    return { itemsWithTotal, subtotal, gstTotal, total }
  }

  const { itemsWithTotal, subtotal, gstTotal, total } = calculateTotals()

  const exportToPDF = () => {
    setLoading(true)
    setTimeout(() => {
      const input = reportRef.current
      html2canvas(input, { scale: 2, useCORS: true }).then((canvas) => {
        const imgData = canvas.toDataURL('image/png')
        const pdf = new jsPDF('p', 'mm', 'a4')
        const imgWidth = 180 // 210mm page width - 15mm margin each side
        const pageHeight = 297
        const imgHeight = (canvas.height * imgWidth) / canvas.width

        let heightLeft = imgHeight
        let position = 10 // start at 10mm from top

        pdf.addImage(imgData, 'PNG', 15, position, imgWidth, imgHeight)
        heightLeft -= pageHeight

        while (heightLeft > 0) {
          position = heightLeft - imgHeight
          pdf.addPage()
          pdf.addImage(imgData, 'PNG', 15, position, imgWidth, imgHeight)
          heightLeft -= pageHeight
        }

        pdf.save(`Tax_Invoice_${invoiceData?.invoice?.number.replace(/ORDER-/i, '')}.pdf`)
        setLoading(false)
      })
    }, 500)
  }

  return (
    <>
      <CCardHeader className="text-center text-white bg-dark">
        <CCol className="px-4 py-1">
          <CRow>
            <CCol md="2">
              <small>STARTING DATE</small>
              <CFormInput
                type="date"
                name="startDate"
                placeholder="Start Date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="px-1 py-0 mt-1"
              />
            </CCol>
            <CCol md="2">
              <small>ENDING DATE</small>
              <CFormInput
                type="date"
                name="endDate"
                placeholder="End Date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="px-1 py-0 mt-1"
              />
            </CCol>
            <CCol className="d-flex justify-content-end p-2">
              <ReactHTMLTableToExcel
                id="test-table-xls-button"
                className="download-table-xls-button btn-sm btn btn-success text-white"
                color="success"
                table="table-to-xls-invoice"
                filename={fileName(`Invoice_Report_${startDate}_${endDate} `)}
                sheet="tablexls"
                buttonText={
                  <>
                    <CIcon icon={cilCloudDownload} className="me-2" /> Invoice Download
                  </>
                }
              />
            </CCol>
          </CRow>
        </CCol>
      </CCardHeader>

      <CTable id="table-to-xls-invoice" className="small-table table-sm" bordered hover responsive>
        <CTableHead color="dark">
          <CTableRow>
            <CTableHeaderCell className="text-center align-middle">#</CTableHeaderCell>
            <CTableHeaderCell className="text-center align-middle">
              <small>Invoice No</small>
            </CTableHeaderCell>
            <CTableHeaderCell className="text-center align-middle">
              <small>Customer Name</small>
            </CTableHeaderCell>
            <CTableHeaderCell className="text-center align-middle">
              <small>Mobile</small>
            </CTableHeaderCell>
            <CTableHeaderCell className="text-center align-middle">
              <small>Order Date</small>
            </CTableHeaderCell>
            <CTableHeaderCell className="text-center align-middle">
              <small>Delivery Date</small>
            </CTableHeaderCell>
            <CTableHeaderCell className="text-center align-middle">
              <small>Payment Mode</small>
            </CTableHeaderCell>
            <CTableHeaderCell className="text-center align-middle">
              <small>Delivery Charges</small>
            </CTableHeaderCell>
            <CTableHeaderCell className="text-center align-middle">
              <small>Coupon Discount</small>
            </CTableHeaderCell>
            <CTableHeaderCell className="text-center align-middle">
              <small>Total Amount</small>
            </CTableHeaderCell>
            <CTableHeaderCell className="text-center align-middle">
              <small>Billing Address</small>
            </CTableHeaderCell>
            <CTableHeaderCell className="text-center align-middle">
              <small>Action</small>
            </CTableHeaderCell>
            <CTableHeaderCell className="text-center align-middle">
              <small>Date & Time Slot</small>
            </CTableHeaderCell>
          </CTableRow>
        </CTableHead>

        <CTableBody>
          {invoices?.map((inv, idx) => (
            <CTableRow key={idx}>
              <CTableDataCell className="text-center align-middle">
                <small>{idx + 1}</small>
              </CTableDataCell>
              <CTableDataCell className="text-center align-middle">
                <small>{inv.order_no.replace(/ORDER-/i, '')}</small>
              </CTableDataCell>
              <CTableDataCell className="text-center align-middle">
                <small>{inv.buyer_name}</small>
              </CTableDataCell>
              <CTableDataCell className="text-center align-middle">
                <small>{inv.buyer_contact}</small>
              </CTableDataCell>
              <CTableDataCell className="text-center align-middle">
                <small>{new Date(inv.order_date).toLocaleDateString()}</small>
              </CTableDataCell>
              <CTableDataCell className="text-center align-middle">
                <small>
                  {inv.deliveryDate ? (
                    new Date(inv.deliveryDate).toLocaleDateString()
                  ) : (
                    <CBadge color="danger" className="m-0">
                      NA
                    </CBadge>
                  )}
                </small>
              </CTableDataCell>
              <CTableDataCell className="text-center align-middle text-uppercase">
                <small>{inv.payment_method}</small>
              </CTableDataCell>
              <CTableDataCell className="text-center align-middle">
                <small>{inv.delivery_charges}</small>
              </CTableDataCell>
              <CTableDataCell className="text-center align-middle">
                <small>{inv.coupons_discount}</small>
              </CTableDataCell>
              <CTableDataCell className="text-center align-middle">
                <small>{inv.total_amount}</small>
              </CTableDataCell>
              <CTableDataCell className="text-center align-middle">
                <small>{inv.billing_address}</small>
              </CTableDataCell>
              <CTableDataCell className="text-center align-middle">
                <CButton
                  color="info"
                  size="sm"
                  className="py-0 px-2 text-sm text-white"
                  onClick={() => {
                    dispatch(getCustomerInvoice(inv.username, inv.order_no))
                    setVisible(true)
                  }}
                >
                  Invoice
                </CButton>
              </CTableDataCell>
              <CTableDataCell className="text-center align-middle">
                <small>{inv.date_slot}</small> <small>{inv.time_slot}</small>
              </CTableDataCell>
            </CTableRow>
          ))}
        </CTableBody>
      </CTable>

      <CModal size="xl" visible={visible} onClose={() => setVisible(false)} scrollable>
        <CModalHeader>
          <strong>Tax Invoice</strong>
        </CModalHeader>
        <CModalBody>
          {loadingInvoices ? (
            <CSpinner size="sm" />
          ) : (
            <div
              ref={reportRef}
              style={{ backgroundColor: 'white', padding: '10mm', fontSize: 14 }}
            >
              <h4 className="text-center my-4">Tax Invoice</h4>
              <CRow className="mb-4">
                <CCol>
                  <h5>Seller</h5>
                  <p>
                    <strong>{invoiceData?.seller?.name}</strong>
                  </p>
                  <p>{invoiceData?.seller?.address}</p>
                  <p>GSTIN: {invoiceData?.seller?.gstin}</p>
                  <p>Phone: {invoiceData?.seller?.contact}</p>
                  <p>Email: {invoiceData?.seller?.email}</p>
                </CCol>
                <CCol>
                  <h5>Buyer</h5>
                  <p>
                    <strong>{invoiceData?.buyer?.name}</strong>
                  </p>
                  <p>{invoiceData?.buyer?.address}</p>
                  <p>Phone: {invoiceData?.buyer?.contact}</p>
                  <p>Email: {invoiceData?.buyer?.email}</p>
                </CCol>
                <CCol>
                  <p>
                    <strong>Invoice No:</strong>{' '}
                    {invoiceData?.invoice?.number.replace(/ORDER-/i, '')}
                  </p>
                  <p>
                    <strong>Order Date:</strong> {DateTimeConverter(invoiceData?.invoice?.date)}
                  </p>
                  {/* <p> <strong>Delivery Date:</strong>{' '}{DateTimeConverter(invoiceData?.invoice?.deliveryDate)}</p> */}
                  <p>
                    <strong>Delivery Date:</strong> {invoiceData?.invoice?.date_slot}
                  </p>
                  <p>
                    <strong>Timing Slot:</strong> {invoiceData?.invoice?.time_slot}
                  </p>
                  <p>
                    <strong>Payment Mode:</strong>{' '}
                    <span className="text-uppercase">{invoiceData?.invoice?.paymentMode}</span>
                  </p>
                  <p>
                    <strong>Order ID:</strong> {invoiceData?.invoice?.orderId}
                  </p>
                </CCol>
              </CRow>

              <CTable>
                <CTableHead>
                  <CTableRow>
                    <CTableHeaderCell>#</CTableHeaderCell>
                    <CTableHeaderCell>Item</CTableHeaderCell>
                    <CTableHeaderCell className="text-center">HSN</CTableHeaderCell>
                    <CTableHeaderCell className="text-center">Qty</CTableHeaderCell>
                    <CTableHeaderCell className="text-center">Rate</CTableHeaderCell>
                    <CTableHeaderCell className="text-center">GST%</CTableHeaderCell>
                    <CTableHeaderCell className="text-center">GST Amt</CTableHeaderCell>
                    <CTableHeaderCell className="text-center">Total</CTableHeaderCell>
                  </CTableRow>
                </CTableHead>
                <CTableBody>
                  {itemsWithTotal?.map((item, index) => (
                    <CTableRow key={index}>
                      <CTableDataCell>{index + 1}</CTableDataCell>
                      <CTableDataCell>{item.name}</CTableDataCell>
                      <CTableDataCell className="text-center">{item.hsn}</CTableDataCell>
                      <CTableDataCell className="text-center">{item.qty}</CTableDataCell>
                      <CTableDataCell className="text-center">₹{item.rate}</CTableDataCell>
                      <CTableDataCell className="text-center">{item.gstPercent}%</CTableDataCell>
                      <CTableDataCell className="text-center">
                        ₹{item.gstAmount.toFixed(2)}
                      </CTableDataCell>
                      <CTableDataCell className="text-center">₹{item.itemTotal}</CTableDataCell>
                    </CTableRow>
                  ))}
                </CTableBody>
              </CTable>

              <CRow className="mt-4">
                <CCol md={7}></CCol>
                <CCol md={5}>
                  <CTable>
                    <CTableBody>
                      <CTableRow>
                        <CTableDataCell>
                          <strong>Subtotal</strong>
                        </CTableDataCell>
                        <CTableDataCell className="text-end px-4">
                          ₹{subtotal?.toFixed(2)}
                        </CTableDataCell>
                      </CTableRow>
                      <CTableRow>
                        <CTableDataCell>
                          <strong>GST Total</strong>
                        </CTableDataCell>
                        <CTableDataCell className="text-end px-4">
                          <small>(Service Tax Included)</small> ₹{gstTotal?.toFixed(2)}
                        </CTableDataCell>
                      </CTableRow>
                      <CTableRow>
                        <CTableDataCell>
                          <strong>Delivery Charges</strong>
                        </CTableDataCell>
                        <CTableDataCell className="text-end px-4">
                          ₹{invoiceData?.deliveryCharge?.toFixed(2)}
                        </CTableDataCell>
                      </CTableRow>
                      <CTableRow>
                        <CTableDataCell>
                          <strong>Coupon Discount</strong>
                        </CTableDataCell>
                        <CTableDataCell className="text-end px-4">
                          -₹{invoiceData?.discount?.toFixed(2)}
                        </CTableDataCell>
                      </CTableRow>
                      <CTableRow>
                        <CTableDataCell>
                          <strong>Grand Total</strong>
                        </CTableDataCell>
                        <CTableDataCell className="text-end px-4">
                          <strong>₹{total?.toFixed(2)}</strong>
                        </CTableDataCell>
                      </CTableRow>
                      <CTableRow>
                        <CTableDataCell colSpan={2}>
                          <em>In Words: {convertToWords(total)}</em>
                        </CTableDataCell>
                      </CTableRow>
                    </CTableBody>
                  </CTable>
                </CCol>
              </CRow>

              <hr />
              <p>
                <strong>Declaration:</strong> We declare that this invoice shows the actual price of
                the goods and is true and correct.
              </p>
              <CRow className="mt-5">
                <CCol md={6}></CCol>
                <CCol md={6} className="text-end">
                  <p>Authorized Signatory</p>
                  <strong>Freshymeat Billing Dept.</strong>
                </CCol>
              </CRow>
            </div>
          )}
        </CModalBody>
        <CModalFooter>
          <CButton color="secondary" onClick={() => setVisible(false)}>
            Close
          </CButton>
          <CButton color="primary" onClick={exportToPDF} disabled={loading}>
            {loading ? <CSpinner size="sm" /> : 'Export as PDF'}
          </CButton>
        </CModalFooter>
      </CModal>
    </>
  )
}

export default TaxInvoice
